import { type OptionWithSelections, type SubOption } from 'ui';

/** Key prefix for every reporting setting stored in the user-config service. */
export const REPORTING_SETTINGS_KEY_PREFIX = 'openfield-web.reporting.';

/** Persisted parameter selection: selected slugs only; the catalog is always rebuilt from the API. */
export type StoredParameterSelection = {
    version: 1;
    /** Slugs in selection order (oldest first), across all groups */
    selected: string[];
    /** Groups shown to the user when saved; kept for compatibility, no longer read */
    knownGroups: string[];
};

const bySelectionTime = (a: SubOption, b: SubOption) => Number(a.selectionTime ?? 0) - Number(b.selectionTime ?? 0);

export const toStoredParameterSelection = (options: OptionWithSelections[]): StoredParameterSelection => ({
    version: 1,
    selected: [
        ...new Set(
            options
                .flatMap((option) => option.selected)
                .sort(bySelectionTime)
                .map((subOption) => subOption.id),
        ),
    ],
    knownGroups: options.map((option) => option.name),
});

const isStoredParameterSelection = (value: unknown): value is StoredParameterSelection => {
    const candidate = value as StoredParameterSelection;
    return candidate?.version === 1 && Array.isArray(candidate.selected) && Array.isArray(candidate.knownGroups);
};

export const parseStoredParameterSelection = (value: unknown): StoredParameterSelection | null =>
    isStoredParameterSelection(value) ? value : null;

export const writeStoredParameterSelection = (storageKey: string, value: StoredParameterSelection) => {
    localStorage.setItem(storageKey, JSON.stringify(value));
};

/** Reads the stored selection, migrating the legacy OptionWithSelections[] shape in place; null when unusable. */
export const readStoredParameterSelection = (storageKey: string): StoredParameterSelection | null => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) {
            return null;
        }

        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            const migrated = toStoredParameterSelection(parsed as OptionWithSelections[]);
            writeStoredParameterSelection(storageKey, migrated);
            return migrated;
        }

        return isStoredParameterSelection(parsed) ? parsed : null;
    } catch (error) {
        console.error('Failed to parse stored parameters:', error);
        return null;
    }
};
